import { FC, useEffect, useState, useCallback, CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { twJoin } from 'tailwind-merge'

export interface OnboardingHighlightOverlayProps {
  targetElementId: string | null
  isActive: boolean
  padding?: number
  borderRadius?: string
  zIndex?: number
  useBoxShadow?: boolean
  backdrop?: {
    backgroundColor?: string
    opacity?: number
    zIndex?: number
  }
  customBackdrop?: (
    props: OnboardingHighlightOverlayProps & {
      highlightStyle: CSSProperties
      elementRect: DOMRect | null
    },
  ) => React.ReactNode
  captureClicksOnHighlightedElement?: boolean
  containerClassName?: string
}

export const OnboardingHighlightOverlay: FC<
  OnboardingHighlightOverlayProps
> = ({
  targetElementId,
  isActive,
  padding = 8,
  borderRadius = '8px',
  zIndex = 9999,
  useBoxShadow = true,
  backdrop,
  customBackdrop = null,
  captureClicksOnHighlightedElement = false,
  containerClassName = 'fixed inset-0 pointer-events-none',
}) => {
  const [elementRect, setElementRect] = useState<DOMRect | null>(null)

  const updateElementRect = useCallback(() => {
    if (!targetElementId) {
      setElementRect(null)
      return
    }

    const element = document.getElementById(targetElementId)
    if (element) {
      let rect = element.getBoundingClientRect()

      // For lesson elements, use a more stable rect to prevent hover issues
      if (targetElementId?.includes('onboarding-first-lesson')) {
        const button = element.querySelector('button')
        if (button) {
          const buttonRect = button.getBoundingClientRect()
          // Create a stable rect based on the button's base dimensions
          rect = {
            top: rect.top,
            bottom: rect.bottom,
            height: rect.height,
            left: buttonRect.left,
            right: buttonRect.left + Math.min(buttonRect.width, 300), // Stable width
            width: Math.min(buttonRect.width, 300),
            x: buttonRect.left,
            y: rect.top,
            toJSON: () => ({}), // Required for DOMRect
          } as DOMRect
        }
      }

      setElementRect(rect)
    } else {
      setElementRect(null)
    }
  }, [targetElementId])

  useEffect(() => {
    if (!isActive) {
      setElementRect(null)
      return
    }

    updateElementRect()

    // Update on scroll or resize
    const handleUpdate = () => updateElementRect()
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    // Use MutationObserver to detect DOM changes
    const isLessonElement = targetElementId?.includes('onboarding-first-lesson')
    let updateTimeout: NodeJS.Timeout | null = null

    const debouncedUpdate = () => {
      if (updateTimeout) clearTimeout(updateTimeout)
      updateTimeout = setTimeout(
        () => {
          handleUpdate()
        },
        isLessonElement ? 100 : 0,
      ) // Debounce updates for lessons
    }

    const observer = new MutationObserver((mutations) => {
      // For lesson elements, be more selective about which mutations trigger updates
      if (isLessonElement) {
        // Only update for structural changes, ignore hover-related attribute changes
        const hasStructuralChanges = mutations.some(
          (mutation) =>
            mutation.type === 'childList' ||
            (mutation.type === 'attributes' &&
              mutation.attributeName !== 'class' &&
              mutation.attributeName !== 'style'),
        )

        if (hasStructuralChanges) {
          debouncedUpdate()
        }
      } else {
        // For non-lesson elements, update immediately for any change
        handleUpdate()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
      observer.disconnect()
      if (updateTimeout) clearTimeout(updateTimeout) // Clean up timeout
    }
  }, [isActive, targetElementId, updateElementRect])

  if (!isActive || !elementRect) {
    return null
  }

  const highlightStyle: CSSProperties = {
    position: 'absolute' as const,
    top: elementRect.top - padding,
    left: elementRect.left - padding,
    width: elementRect.width + padding * 2,
    height: elementRect.height + padding * 2,
    borderRadius,
  }

  const overlayContent = (
    <div className={containerClassName} style={{ zIndex }}>
      {/* Dark backdrop overlay - covers entire screen */}
      {backdrop && (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: backdrop.backgroundColor || 'rgba(0, 0, 0, 0.5)',
            opacity: backdrop.opacity || 1,
            zIndex: backdrop.zIndex || zIndex - 1,
          }}
        />
      )}

      {/* Highlighted area cutout - creates a "hole" in the backdrop */}
      {backdrop && elementRect && (
        <div
          className={twJoin(
            'absolute',
            captureClicksOnHighlightedElement
              ? 'cursor-pointer'
              : 'pointer-events-none',
          )}
          onClick={
            captureClicksOnHighlightedElement && targetElementId
              ? () => {
                  const element = document.getElementById(targetElementId)
                  if (element) {
                    element.click()
                  }
                }
              : undefined
          }
          style={{
            ...highlightStyle,
            backgroundColor: 'transparent',
            zIndex: backdrop.zIndex || zIndex,
            boxShadow: `0 0 0 10000px ${
              backdrop.backgroundColor || 'rgba(0, 0, 0, 0.5)'
            }`,
          }}
        />
      )}

      {/* Subtle highlighted area border - shown when no backdrop */}
      <div
        className={twJoin(
          'absolute pointer-events-none',
          useBoxShadow && 'bg-v8-blue/5 border-2 border-v8-blue',
        )}
        style={{
          ...highlightStyle,
          ...(useBoxShadow
            ? {
                boxShadow:
                  '0 0 0 2px rgba(99, 102, 241, 0.2), 0 0 12px rgba(99, 102, 241, 0.3)',
                animation: 'onboarding-pulse 2s ease-in-out infinite',
              }
            : {}),
        }}
      />

      {/* Add CSS animation for subtle pulsing */}
      <style jsx>{`
        @keyframes onboarding-pulse {
          0%,
          100% {
            box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2),
              0 0 12px rgba(99, 102, 241, 0.3);
          }
          50% {
            box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.3),
              0 0 20px rgba(99, 102, 241, 0.4);
          }
        }
      `}</style>
    </div>
  )

  if (customBackdrop) {
    return createPortal(
      customBackdrop({
        targetElementId,
        isActive,
        padding,
        borderRadius,
        zIndex,
        useBoxShadow,
        backdrop,
        customBackdrop,
        captureClicksOnHighlightedElement,
        highlightStyle,
        elementRect,
      }),
      document.body,
    )
  }

  return createPortal(overlayContent, document.body)
}
